import { useState, useRef, useEffect } from "react";
import { Bell, CheckCheck, Settings } from "lucide-react";
import { useNotifications } from "@/contexts/NotificationContext";
import { useNavigate } from "react-router-dom";

const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = (notification) => {
    if (!notification.read) markAsRead(notification.id);
    if (notification.article_url) window.open(notification.article_url, "_blank");
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const diff = Math.floor((new Date() - date) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 transition-colors"
        style={{ color: "var(--color-text-muted)" }}
        data-testid="notification-bell"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse" style={{ backgroundColor: "var(--color-live)" }}>
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 rounded-xl shadow-2xl z-50 max-h-[500px] overflow-hidden flex flex-col" style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
          <div className="p-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--color-border)" }}>
            <h3 className="font-bold text-lg" style={{ color: "var(--color-text)" }}>Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={markAllAsRead} className="text-xs flex items-center gap-1" style={{ color: "var(--color-accent)" }} data-testid="mark-all-read">
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button onClick={() => { navigate("/settings"); setIsOpen(false); }} className="transition-colors" style={{ color: "var(--color-text-dim)" }} data-testid="notification-settings">
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center" style={{ color: "var(--color-text-dim)" }}>
                <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No notifications yet</p>
                <p className="text-sm mt-1">We'll notify you when there's breaking news</p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="w-full p-4 text-left transition-colors"
                    style={{ borderBottom: "1px solid var(--color-border)", backgroundColor: !notification.read ? "var(--color-accent-glow)" : "transparent" }}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {!notification.read && (
                        <div className="w-2 h-2 rounded-full mt-2" style={{ backgroundColor: "var(--color-accent)" }}></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="font-semibold text-sm line-clamp-1" style={{ color: "var(--color-text)" }}>{notification.title}</h4>
                          <span className="text-xs whitespace-nowrap" style={{ color: "var(--color-text-dim)" }}>{formatTime(notification.created_at)}</span>
                        </div>
                        <p className="text-sm line-clamp-2" style={{ color: "var(--color-text-muted)" }}>{notification.message}</p>
                        {notification.category && (
                          <span className="inline-block mt-2 px-2 py-0.5 text-xs rounded" style={{ backgroundColor: "var(--color-card-hover)", color: "var(--color-accent)" }}>{notification.category}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
